import { qaLabelDispatch } from '../../../../../common-qa-labels/dispatch';
import { localDateString } from '@vromo/dashboard/src/common/locale';
import timeStore from '@vromo/dashboard/src/stores/TimeStore';
import { InputLabel } from '@vromo/ui';
import Input from '@vromo/ui/components/Input';
import RadioGroup from '@vromo/ui/components/Radio';
import addMinutes from 'date-fns/add_minutes';
import format from 'date-fns/format';
import { css } from 'emotion';
import React from 'react';
import DateTime from 'react-datetime';
import { useTranslation } from 'react-i18next';
import { DEFAULT_PREP_TIME } from '../../JobForm';
import styles from './pickupTime.style';
import { observer } from 'mobx-react-lite';
import moment from 'moment';

interface Props {
    scheduledDate: number | undefined;
    prepTime: number | undefined;
    setScheduledDate: (scheduledDate: number | undefined) => void;
    setPrepTime: (prepTime: number | undefined) => void;
}

const PickupTime = observer(({ scheduledDate, prepTime, setScheduledDate, setPrepTime }: Props) => {
    const { t } = useTranslation();

    return (
        <InputLabel label={t('jobRequestForm.pickupTime')} optional noLabelComponent>
            <div
                className={css`
                    display: flex;
                    align-items: center;

                    @media (max-width: 400px) {
                        flex-direction: column;
                    }
                `}>
                <div
                    style={{
                        display: 'flex',
                        flex: 3,
                        flexDirection: 'column'
                    }}>
                    <RadioGroup
                        name="deadlineType"
                        direction="row"
                        labelStyle={styles.deadlineRadioLabel}
                        options={[
                            {
                                component: (
                                    <div style={styles.deadlineOption}>
                                        <Input
                                            data-testid={qaLabelDispatch.newJobModal.pickupNowInput}
                                            label={t('jobRequestForm.now')}
                                            smalllabel={t('jobRequestForm.prepTime')}
                                            type="number"
                                            step="1"
                                            min="0"
                                            value={prepTime ?? ''}
                                            placeholder={t('jobRequestForm.prepTime')}
                                            onChange={({ target }) => {
                                                setPrepTime(
                                                    target.value
                                                        ? parseInt(target.value)
                                                        : undefined
                                                );
                                            }}
                                            disabled={scheduledDate !== undefined}
                                        />
                                        <div
                                            style={{
                                                visibility: scheduledDate ? 'hidden' : 'visible'
                                            }}>
                                            {t('jobRequestForm.pickupAt', {
                                                time: prepTime
                                                    ? `${t('common.at')} ${format(
                                                          addMinutes(timeStore.slowTime, prepTime),
                                                          'h:mm a'
                                                      )}`
                                                    : t('common.asap')
                                            })}
                                        </div>
                                    </div>
                                ),
                                label: t('jobRequestForm.now'),
                                value: 'now'
                            },
                            {
                                label: t('jobRequestForm.schedule'),
                                component: (
                                    <div style={styles.deadlineOption}>
                                        <DateTime
                                            key={prepTime} // https://github.com/arqex/react-datetime/issues/760
                                            value={
                                                scheduledDate ? new Date(scheduledDate) : undefined
                                            }
                                            onChange={(it) => {
                                                if (moment.isMoment(it)) {
                                                    setScheduledDate(it.valueOf());
                                                }
                                            }}
                                            dateFormat={localDateString}
                                            timeFormat="hh:mm A"
                                            renderInput={(props) => (
                                                <Input
                                                    {...props}
                                                    data-testid={
                                                        qaLabelDispatch.newJobModal.scheduleInput
                                                    }
                                                    label={t('jobRequestForm.schedule')}
                                                />
                                            )}
                                            inputProps={{
                                                disabled: !scheduledDate,
                                                placeholder: t('jobRequestForm.scheduledDate')
                                            }}
                                            className={css`
                                                flex: 1;
                                            `}
                                        />
                                        <div
                                            style={{
                                                visibility: scheduledDate ? undefined : 'hidden'
                                            }}>
                                            {scheduledDate &&
                                                t('jobRequestForm.pickupAt', {
                                                    time: `${t('common.at')} ${format(
                                                        scheduledDate.valueOf(),
                                                        'h:mm a'
                                                    )}`
                                                })}
                                        </div>
                                    </div>
                                ),
                                value: 'scheduled'
                            }
                        ]}
                        onChange={({ target }) => {
                            if (target.value === 'scheduled') {
                                setScheduledDate(Date.now());
                                setPrepTime(undefined);
                            } else {
                                setScheduledDate(undefined);
                                setPrepTime(DEFAULT_PREP_TIME);
                            }
                        }}
                        checked={scheduledDate ? 'scheduled' : 'now'}
                    />
                </div>
            </div>
        </InputLabel>
    );
});

export default PickupTime;
